// src/components/NavigationTooltip.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './NavigationTooltip.module.css';
import tooltipManager from '../utils/tooltipManager';
import TooltipPortal from '../utils/TooltipPortal';

const NavigationTooltip = ({ children, text, disabled = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);
  const tooltipId = useRef(`nav-tooltip-${Math.random().toString(36).substr(2, 9)}`);
  const updateTimeoutRef = useRef(null);

  const updatePosition = useCallback((clientX, clientY) => {
    // Clear any pending update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    if (!triggerRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    
    // If element doesn't have valid dimensions, use mouse position as fallback
    if (!triggerRect || triggerRect.width === 0 || triggerRect.height === 0) {
      if (clientX && clientY) {
        setPosition({
          x: Math.max(10, Math.min(clientX + 10, window.innerWidth - 300)),
          y: Math.max(10, Math.min(clientY - 40, window.innerHeight - 120))
        });
      }
      return;
    }

    // Dynamic tooltip sizing based on screen size
    const isMobile = window.innerWidth <= 768;
    const tooltipWidth = isMobile ? Math.min(280, window.innerWidth - 20) : Math.min(350, window.innerWidth - 20);
    const tooltipHeight = 120;
    const padding = 10;

    // Calculate initial position (prefer right side of button)
    let x = triggerRect.right + padding;
    let y = triggerRect.top + (triggerRect.height / 2) - (tooltipHeight / 2);

    // Check if tooltip would go off-screen horizontally
    if (x + tooltipWidth > window.innerWidth - padding) {
      x = triggerRect.left - tooltipWidth - padding; // Position to left
      
      // If left position also goes off-screen, center above/below
      if (x < padding) {
        x = Math.max(padding, triggerRect.left + (triggerRect.width / 2) - (tooltipWidth / 2));
        // Position below if fits, otherwise above
        if (triggerRect.bottom + tooltipHeight + padding < window.innerHeight) {
          y = triggerRect.bottom + padding;
        } else {
          y = triggerRect.top - tooltipHeight - padding;
        }
      }
    }

    // Ensure tooltip doesn't go off top or bottom of screen
    if (y < padding) {
      y = triggerRect.bottom + padding; // Position below
    } else if (y + tooltipHeight > window.innerHeight - padding) {
      y = triggerRect.top - tooltipHeight - padding; // Position above
    }

    // Final boundary enforcement
    x = Math.max(padding, Math.min(x, window.innerWidth - tooltipWidth - padding));
    y = Math.max(padding, Math.min(y, window.innerHeight - tooltipHeight - padding));

    // Final validation with fallback
    if (isNaN(x) || isNaN(y) || x < 0 || y < 0) {
      x = Math.max(padding, Math.min((clientX || triggerRect.right) - tooltipWidth / 2, window.innerWidth - tooltipWidth - padding));
      y = Math.max(padding, Math.min((clientY || triggerRect.bottom) + padding, window.innerHeight - tooltipHeight - padding));
    }

    setPosition({ x, y });
  }, []);

  const handleMouseEnter = useCallback((e) => {
    if (window.innerWidth > 768) { // Only show on hover for desktop
      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      // Add a small delay to ensure DOM is ready
      updateTimeoutRef.current = setTimeout(() => {
        if (triggerRef.current) {
          updatePosition(e.clientX, e.clientY);
          tooltipManager.show(tooltipId.current);
          setIsVisible(true);
        }
      }, 100); // Increased delay for better reliability
    }
  }, [updatePosition]);

  const handleMouseLeave = useCallback(() => {
    if (window.innerWidth > 768) {
      // Clear any pending update
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
      
      tooltipManager.hide(tooltipId.current);
      setIsVisible(false);
    }
  }, []);

  const handleTouchStart = useCallback((e) => {
    // Don't prevent default - allow the underlying button to be clicked
    if (e.touches && e.touches[0]) {
      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      updateTimeoutRef.current = setTimeout(() => {
        if (triggerRef.current) {
          updatePosition(e.touches[0].clientX, e.touches[0].clientY);
          tooltipManager.show(tooltipId.current);
          setIsVisible(true);
          // Hide tooltip after a short delay
          setTimeout(() => {
            tooltipManager.hide(tooltipId.current);
            setIsVisible(false);
          }, 1500);
        }
      }, 100);
    }
  }, [updatePosition]);

  const handleTouchEnd = useCallback(() => {
    // Touch end doesn't need to do anything now since we auto-hide
  }, []);

  // Register with tooltip manager
  useEffect(() => {
    const currentTooltipId = tooltipId.current; // Copy to variable for cleanup
    const hideTooltip = () => setIsVisible(false);
    tooltipManager.register(currentTooltipId, hideTooltip);
    
    return () => {
      tooltipManager.unregister(currentTooltipId);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  // Close tooltip when clicking outside or pressing escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        tooltipManager.hide(tooltipId.current);
        setIsVisible(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        tooltipManager.hide(tooltipId.current);
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isVisible]);

  if (disabled || !text) {
    return children;
  }

  return (
    <>
      <div
        ref={triggerRef}
        className={styles.tooltipTrigger}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
      
      <TooltipPortal isVisible={isVisible}>
        <div
          ref={tooltipRef}
          className={styles.tooltip}
          style={{ 
            left: `${position.x}px`, 
            top: `${position.y}px`,
          }}
        >
          <div className={styles.tooltipContent}>
            {text}
          </div>
        </div>
      </TooltipPortal>
    </>
  );
};

export default NavigationTooltip; 